import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Zap, Loader2, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth.jsx';
import { api, saveTokens } from '../lib/api.js';

/**
 * The three pages reached from an emailed link.
 *
 * All of them must work with no session — the recipient may be on a different
 * machine, or have no account yet — so they live outside the authenticated
 * shell and take the token from the query string.
 */

function Frame({ title, children }) {
  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <Zap size={17} className="brand__bolt" />
          <span className="brand__name">Superchat</span>
        </div>
        <h1 className="login__title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ verify email */

export function VerifyEmailView() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const token = params.get('token');
  const [state, setState] = useState({ phase: token ? 'working' : 'error', error: null });

  useEffect(() => {
    if (!token) {
      setState({ phase: 'error', error: 'This link is missing its token.' });
      return;
    }

    let cancelled = false;
    api.auth
      .verifyEmail(token)
      .then((data) => {
        if (cancelled) return;
        // Verifying returns a fresh token pair, so the click also signs them
        // in — on a new machine that saves a pointless second step.
        saveTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
        });
        setUser(data.user);
        setState({ phase: 'done', error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ phase: 'error', error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [token, setUser]);

  if (state.phase === 'working') {
    return (
      <Frame title="Confirming your address">
        <p className="login__sub">
          <Loader2 size={14} className="spin" /> One moment…
        </p>
      </Frame>
    );
  }

  if (state.phase === 'error') {
    return (
      <Frame title="That link did not work">
        <div className="login__error">
          <XCircle size={13} /> {state.error}
        </div>
        <p className="login__sub">
          Confirmation links expire after 24 hours and work only once. Sign in and request a
          fresh one from the banner at the top of the dashboard.
        </p>
        <Link className="button button--primary" to="/login">
          Go to sign in
        </Link>
      </Frame>
    );
  }

  return (
    <Frame title="Address confirmed">
      <div className="result result--ok">
        <CheckCircle2 size={13} /> Your email address is verified and you are signed in.
      </div>
      <button
        type="button"
        className="button button--primary"
        onClick={() => navigate('/chat', { replace: true })}
      >
        Open the console
      </button>
    </Frame>
  );
}

/* ---------------------------------------------------------- reset password */

export function ResetPasswordView() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    // Checked here as well as by the field, because a mismatch that only
    // surfaces after the single-use token is spent cannot be retried.
    if (password !== confirm) {
      setError('Those passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await api.auth.resetPassword(token, password);
      saveTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      setUser(data.user);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Frame title="That link is incomplete">
        <div className="login__error">This reset link is missing its token.</div>
        <Link className="button button--primary" to="/login">
          Request a new one
        </Link>
      </Frame>
    );
  }

  if (done) {
    return (
      <Frame title="Password changed">
        <div className="result result--ok">
          <ShieldCheck size={13} /> You are signed in here. Every other session was signed out.
        </div>
        <button
          type="button"
          className="button button--primary"
          onClick={() => navigate('/chat', { replace: true })}
        >
          Open the console
        </button>
      </Frame>
    );
  }

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <div className="login__brand">
          <Zap size={17} className="brand__bolt" />
          <span className="brand__name">Superchat</span>
        </div>
        <h1 className="login__title">Choose a new password</h1>
        <p className="login__sub">
          At least 8 characters. Saving this signs you out of every other browser.
        </p>

        <label className="field">
          <span className="field__label">NEW PASSWORD</span>
          <input
            className="field__control"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </label>

        <label className="field">
          <span className="field__label">CONFIRM</span>
          <input
            className="field__control"
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </label>

        {error && <div className="login__error">{error}</div>}

        <button className="button button--primary" type="submit" disabled={busy}>
          {busy && <Loader2 size={14} className="spin" />} Save password
        </button>
      </form>
    </div>
  );
}

/* ----------------------------------------------------------- accept invite */

const SCOPE_LABEL = {
  chat: 'Chat',
  docs: 'Docs',
  analytics: 'Analytics',
  integration: 'Integration',
};

const LEVEL_LABEL = {
  none: 'No access',
  read: 'Read only',
  send: 'Read and reply',
  write: 'Read and edit',
};

/**
 * Shows what is being offered, then accepts it.
 *
 * One invitation can cover several apps, each with its own grant, so the offer
 * is rendered app by app rather than as one lump — accepting is a single
 * decision, but it has to be an informed one about every app in it.
 *
 * The invite is bound to an address, so someone signed in as anyone else is
 * told plainly rather than being handed a button that will 403. Someone with no
 * account at all is sent to register — the token survives in the URL, so they
 * come back here once they have one.
 */
export function AcceptInviteView() {
  const [params] = useSearchParams();
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const token = params.get('token');

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('This invitation link is missing its token.');
      return;
    }
    api.invites
      .preview(token)
      .then(setInvite)
      .catch((err) => setError(err.message));
  }, [token]);

  const accept = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setAccepted(await api.invites.accept(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [token]);

  if (error && !invite) {
    return (
      <Frame title="That invitation did not work">
        <div className="login__error">
          <XCircle size={13} /> {error}
        </div>
        <p className="login__sub">
          Invitations expire after seven days and work only once. Ask whoever invited you to
          send a fresh one.
        </p>
        <Link className="button button--primary" to="/login">
          Go to sign in
        </Link>
      </Frame>
    );
  }

  if (!invite) {
    return (
      <Frame title="Checking your invitation">
        <p className="login__sub">
          <Loader2 size={14} className="spin" /> One moment…
        </p>
      </Frame>
    );
  }

  // `apps` is the shape every invitation has; a one-app invite is a list of one.
  const offered = invite.apps ?? [
    { appId: null, appName: invite.appName, permissions: invite.permissions },
  ];

  if (accepted) {
    const landed = accepted.apps ?? offered;
    return (
      <Frame
        title={
          landed.length === 1 ? `You are in — ${landed[0].appName}` : `You are in — ${landed.length} apps`
        }
      >
        <div className="result result--ok">
          <CheckCircle2 size={13} /> Access granted. It is live now, no sign-out needed.
        </div>
        {landed.length > 1 && (
          <div className="settings">
            {landed.map((entry) => (
              <div className="settings__row" key={entry.appId ?? entry.appName}>
                <span>{entry.appName}</span>
                <span className="muted">
                  {entry.alreadyMember ? 'already had access' : 'added'}
                </span>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          className="button button--primary"
          onClick={() => navigate('/chat', { replace: true })}
        >
          Open the console
        </button>
      </Frame>
    );
  }

  const signedInAsSomeoneElse = status === 'authenticated' && user?.email !== invite.email;

  return (
    <Frame
      title={offered.length === 1 ? `Join ${offered[0].appName}` : `Join ${offered.length} apps`}
    >
      <p className="login__sub">
        You have been invited as <strong>{invite.email}</strong> with this access:
      </p>

      {offered.map((entry) => {
        const grants = Object.entries(entry.permissions).filter(([, level]) => level !== 'none');
        return (
          <div className="settings" key={entry.appId ?? entry.appName}>
            {/* Named even when there is only one, so the grant below is never
                ambiguous about which app it belongs to. */}
            <div className="settings__row">
              <strong>{entry.appName}</strong>
            </div>
            {grants.length === 0 && (
              <div className="muted">No areas granted yet — the owner can widen this later.</div>
            )}
            {grants.map(([scope, level]) => (
              <div className="settings__row" key={scope}>
                <span className="muted">{SCOPE_LABEL[scope] ?? scope}</span>
                <span className="mono">{LEVEL_LABEL[level] ?? level}</span>
              </div>
            ))}
          </div>
        );
      })}

      {error && <div className="login__error">{error}</div>}

      {status === 'anonymous' && (
        <>
          <p className="login__sub">
            Sign in as {invite.email} to accept — or create that account first. Come back to
            this link afterwards.
          </p>
          <Link className="button button--primary" to="/login">
            Sign in or create an account
          </Link>
        </>
      )}

      {signedInAsSomeoneElse && (
        <>
          <div className="result result--warn">
            You are signed in as {user.email}. This invitation only works for {invite.email}.
          </div>
          <button type="button" className="button" onClick={logout}>
            Sign out and switch account
          </button>
        </>
      )}

      {status === 'authenticated' && !signedInAsSomeoneElse && (
        <button
          type="button"
          className="button button--primary"
          onClick={accept}
          disabled={busy}
        >
          {busy && <Loader2 size={14} className="spin" />} Accept invitation
        </button>
      )}
    </Frame>
  );
}

export default { VerifyEmailView, ResetPasswordView, AcceptInviteView };
